import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSetUserPlan, type AdminUser } from "@/hooks/useAdminUsers";

type CompPlan = "beta" | "pro" | "institutional";

const COMP_PLANS: { value: CompPlan; label: string; blurb: string }[] = [
  {
    value: "beta",
    label: "Beta tester",
    blurb: "Paid features, FD builds capped at 10/mo. Checkout stays open for conversion.",
  },
  {
    value: "pro",
    label: "Pro",
    blurb: "Full individual paid features.",
  },
  {
    value: "institutional",
    label: "Institutional",
    blurb: "Paid + institutional entitlements.",
  },
];

interface Props {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
}

export function GrantCompDialog({ user, open, onClose }: Props) {
  const setPlan = useSetUserPlan();
  const initialFor = (current: string | undefined): CompPlan =>
    current === "institutional" || current === "pro" ? current : "beta";
  const [plan, setPlanChoice] = useState<CompPlan>(initialFor(user?.plan));

  // Reset the picker when the target user changes / modal re-opens.
  useEffect(() => {
    if (open) {
      setPlanChoice(initialFor(user?.plan));
    }
  }, [open, user?.id, user?.plan]);

  function handleSubmit() {
    if (!user) return;
    setPlan.mutate({ userId: user.id, plan });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant complimentary plan</DialogTitle>
          <DialogDescription>
            {user
              ? `Bestow a paid plan on ${user.email} without Stripe billing.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-2">
          <legend className="sr-only">Choose plan</legend>
          {COMP_PLANS.map((opt) => {
            const id = `grant-comp-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={id}
                className={
                  "flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm transition-colors " +
                  (plan === opt.value
                    ? "border-primary bg-primary/5"
                    : "hover:bg-muted/50")
                }
              >
                <input
                  id={id}
                  type="radio"
                  name="grant-comp-plan"
                  value={opt.value}
                  checked={plan === opt.value}
                  onChange={() => setPlanChoice(opt.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{opt.label}</span>
                  <span className="ml-2 text-muted-foreground">
                    {opt.blurb}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!user}>
            Grant {COMP_PLANS.find((p) => p.value === plan)?.label ?? plan}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GrantCompDialog;
